import * as React from 'react'

import { Box, Grid, Spinner, Text } from '@factorialco/deprecated-design-system'
import { ContractOverviewCompensationsDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import Header from 'modules/contracts/components/ContractDetails/Section/Header'
import type CompensationModel from 'modules/contracts/store/models/Compensation'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'
import type Employee from 'modules/employees/store/models/Employee'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import { AdditionalCompensationsList } from '../../FlowPage/Steps/RoleLevelSalary/_AdditionalCompensations/AdditionalCompensationsList/AdditionalCompensationsList'
import AdditionalCompensations from './AdditionalCompensations'
import Salary from './Salary'

interface Props {
  contractVersion: ContractVersion
  compensations: CompensationModel[]
  employee: Employee
}

const Compensation = ({ contractVersion, compensations, employee }: Props) => {
  const { company } = useCurrentSession()
  const {
    data: contractsCompensations,
    loading,
    error,
  } = useQuery(ContractOverviewCompensationsDocument, {
    variables: {
      contractVersionId: contractVersion.id,
      companyId: company.id,
    },
  })
  const isNewAdditionalCompensationsSupportEnabled = error
    ? false
    : contractsCompensations?.features?.featuresConnection?.nodes?.[0]?.enabled
  const canEdit = policyAuthorizations.for('contracts.edit', employee)
  const canSeeAdditionalCompensations = policyAuthorizations.for(
    'contracts.see_additional_compensation',
    employee
  )
  const canSeeGrossSalary = policyAuthorizations.for('contracts.see_gross_salary', employee)
  const canSeePayrollPolicy = policyAuthorizations.for('contracts.see_payroll_policy', employee)

  if (!canSeeAdditionalCompensations && !canSeeGrossSalary && !canSeePayrollPolicy) return null
  const isCompensationsEmpty = compensations.length === 0
  const showAdditionalCompensations =
    canSeeAdditionalCompensations && (!isCompensationsEmpty || canEdit)

  if (loading) {
    return (
      <Box
        gap="s8"
        padding="s48"
        alignItems="center"
        data-testid="additional-compensations-loading"
      >
        <Spinner size="s32" data-testid="loading" />
      </Box>
    )
  }

  const additionalCompensations = isNewAdditionalCompensationsSupportEnabled
    ? contractsCompensations?.contracts.additionalCompensationsConnection.nodes
    : []
  const contractPayrollPolicy = contractsCompensations?.contracts.contractVersion?.payrollPolicy

  return (
    <Box gap="s16" flexDirection="column">
      <Header icon="payroll" title={i18n.t('contract_versions.compensation.title')} />
      <Grid variant="fixed">
        <Box gridColSpan={{ xxs: 12, xs: 12, s: showAdditionalCompensations ? 6 : 12 }} gap="s8">
          <Salary
            contractVersion={contractVersion}
            employee={employee}
            isNewAdditionalCompensationsSupportEnabled={isNewAdditionalCompensationsSupportEnabled}
            contractPayrollPolicy={contractPayrollPolicy}
          />
        </Box>
        {showAdditionalCompensations && (
          <Box gridColSpan={{ xxs: 12, xs: 12, s: 6 }} gap="s8">
            <AdditionalCompensations
              compensations={compensations}
              employee={employee}
              additionalCompensationsList={
                isNewAdditionalCompensationsSupportEnabled &&
                additionalCompensations &&
                additionalCompensations.length > 0 ? (
                  <AdditionalCompensationsList additionalCompensations={additionalCompensations} />
                ) : additionalCompensations && additionalCompensations.length > 0 ? (
                  <Text size="300" weight="medium" color="grey600">
                    {i18n.t('contract_versions.compensation.not_informed')}
                  </Text>
                ) : null
              }
            />
          </Box>
        )}
      </Grid>
    </Box>
  )
}

export default observer(Compensation)
